/* eslint react/prop-types: 0 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography
} from '@mui/material';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DataView from '../../v2/components/DataView';
import ApiService from '../../common/ApiCalls';
import createUuid from '../../common/createUuid';
import {
  correctionError,
  editorValue,
  historyPermissions,
  typedValue
} from './historyCorrections';

export default function HistoricalCard({
  card,
  rows,
  context,
  subjectId,
  canEdit,
  onSaved,
  onReload
}) {
  const items = card.items.filter(
    (item) => item.item_type === 'field' && item.field
  );
  const editable = items.filter((item) =>
    rows.some(
      (row) =>
        row.field_id === item.field_id && row.data_state !== 'incompatible'
    )
  );
  const initial = Object.fromEntries(
    items.map((item) => [
      item.field_id,
      editorValue(
        item.field,
        rows.find((row) => row.field_id === item.field_id)?.value
      )
    ])
  );
  const states = Object.fromEntries(
    rows.map((row) => [row.field_id, row.data_state])
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const [clears, setClears] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const request = useRef(null);
  const inFlight = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const save = async () => {
    if (inFlight.current) return;
    if (!request.current) {
      const changed = editable.filter(
        (item) =>
          clears[item.field_id] ||
          JSON.stringify(draft[item.field_id]) !==
            JSON.stringify(initial[item.field_id])
      );
      if (!changed.length) {
        setEditing(false);
        return;
      }
      if (changed.length > 25) {
        setError('Save up to 25 changed fields at a time.');
        return;
      }
      request.current = {
        idempotency_key: createUuid(),
        values: changed.map((item) => {
          const row = rows.find((value) => value.field_id === item.field_id);
          const value = typedValue(item.field, draft[item.field_id]);
          const clear =
            !!clears[item.field_id] || value === '' || value === null;
          return {
            subject_type: row.subject_type,
            subject_id: subjectId,
            object_type: row.object_type,
            object_id: row.object_id ?? null,
            assignment_period_id: row.assignment_period_id ?? null,
            field_id: item.field_id,
            as_of_date: context.as_of_date,
            timezone: context.timezone,
            effective_day: context.target_effective_day,
            history_timezone: context.history_timezone,
            target_effective_at: row.target_effective_at ?? null,
            expected_target_version: row.target_version ?? null,
            expected_resolved_version: row.resolved_version ?? null,
            value: clear ? null : value,
            is_clear: clear
          };
        })
      };
    }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const records = await ApiService().submitHistoryCorrection(
        request.current
      );
      if (active.current)
        onSaved(
          records.some((row) => row.status === 'rejected')
            ? 'A correction was rejected. History has been refreshed.'
            : records.some((row) => row.status === 'pending_approval')
              ? 'Changes submitted for approval. Approved values are unchanged.'
              : 'Changes applied. History has been refreshed.'
        );
    } catch (e) {
      if (active.current) {
        setError(correctionError(e));
        const ambiguous = !e?.response?.status || e.response.status >= 500;
        setUncertain(ambiguous);
        setConflict([400, 403, 404, 409].includes(e?.response?.status));
        if (!ambiguous) request.current = null;
      }
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  };
  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6">{card.title}</Typography>
        {!editing && canEdit && editable.length > 0 && (
          <IconButton
            aria-label={`Edit ${card.title}`}
            onClick={() => {
              setDraft(initial);
              setClears({});
              setError(null);
              setConflict(false);
              setUncertain(false);
              request.current = null;
              setEditing(true);
            }}
          >
            <EditOutlined />
          </IconButton>
        )}
      </Box>
      {editing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Effective {context.target_effective_day} ({context.history_timezone}).{' '}
          {historyPermissions(subjectId).bypass
            ? 'Your historical admin permission applies changes immediately.'
            : 'Past changes require approval.'}{' '}
          Later recorded changes are preserved.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {editing ? (
        <fieldset
          disabled={busy || uncertain || conflict}
          style={{ border: 0, padding: 0, margin: 0 }}
        >
          {!(busy || uncertain || conflict) && (
            <DataView
              card={{ ...card, items: editable }}
              orientation="horizontal"
              isEdit
              values={draft}
              handleChange={({ target }) =>
                setDraft((values) => ({
                  ...values,
                  [target.name]: target.value
                }))
              }
              handleBlur={() => {}}
            />
          )}
          {editable.map((item) => (
            <FormControlLabel
              key={item.field_id}
              control={
                <Checkbox
                  checked={!!clears[item.field_id]}
                  onChange={(e) =>
                    setClears((values) => ({
                      ...values,
                      [item.field_id]: e.target.checked
                    }))
                  }
                />
              }
              label={`Clear ${item.field.label}`}
            />
          ))}
        </fieldset>
      ) : (
        <DataView
          card={{ ...card, items }}
          orientation="horizontal"
          isEdit={false}
          values={initial}
          historyStates={states}
        />
      )}
      {editing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button disabled={busy} onClick={() => setEditing(false)}>
            Cancel
          </Button>
          {conflict ? (
            <Button onClick={onReload}>Reload history</Button>
          ) : (
            <Button variant="contained" disabled={busy} onClick={save}>
              {busy ? 'Saving...' : uncertain ? 'Retry save' : 'Save'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
